import { cn } from '@agentic-flow-studio/shared/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@agentic-flow-studio/shared/ui/alert-dialog';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import {
  AlertCircle,
  ArrowLeft,
  FolderTree,
  Globe,
  Loader2,
  Mail,
  Minus,
  Plus,
  RefreshCw,
  Sparkles,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AnalysisSettings } from '@/components/AnalysisSettings';
import { MailTagsSettings } from '@/components/MailTagsSettings';
import type {
  AccountPermission,
  ClinicAccount,
  ClinicCategory,
  WorkspaceMember,
} from '@/types/clinic';
import { clinicApi } from '@/utils/api';
import { trpc } from '@/utils/trpc';

export default function ClinicSettings() {
  const navigate = useNavigate();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const [activeTab, setActiveTab] = useState<
    'domains' | 'categories' | 'accounts' | 'mailTags' | 'analysis'
  >('categories');
  const [isLoading, setIsLoading] = useState(true);
  const [isSuggestingCats, setIsSuggestingCats] = useState(false);

  const [domainWeights, setDomainWeights] = useState<Record<string, number>>({});

  const [categories, setCategories] = useState<ClinicCategory[]>([]);
  const [accounts, setAccounts] = useState<ClinicAccount[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);

  // Mail tags (Gmail labels/Outlook categories + their weights/mappings) are
  // fetched and managed entirely within MailTagsSettings now — the settings-tab
  // cutover moved that data off this page's single-account REST fetch.
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [domainWeightsData, categoriesData, accountsData, membersData] = await Promise.all([
        clinicApi.getDomainWeights().catch(() => []),
        clinicApi.getCategories().catch(() => []),
        clinicApi.getMailAccounts().catch(() => []),
        clinicApi.getWorkspaceMembers().catch(() => []),
      ]);

      const dWeights: Record<string, number> = {};
      domainWeightsData.forEach((w) => {
        dWeights[w.domain] = w.weight;
      });
      setDomainWeights(dWeights);

      setCategories(categoriesData);
      setAccounts(accountsData);
      setMembers(membersData);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateDomainWeight = async (domain: string, delta: number) => {
    if (isReadOnly) return;
    const current = domainWeights[domain] || 0;
    const next = Math.max(-10, Math.min(10, current + delta));
    setDomainWeights((prev) => ({ ...prev, [domain]: next }));
    try {
      await clinicApi.updateDomainWeight(domain, next);
    } catch {
      toast.error('Sync failed');
    }
  };

  const handleAddCategory = async () => {
    if (isReadOnly) return;
    const name = prompt('Category name:');
    if (!name) return;
    try {
      // calm-interface-ignore: a stored default for a user-chosen category colour, not UI styling
      const newCat = await clinicApi.updateCategory({ name, color: '#3b82f6', weight: 0 });
      setCategories((prev) => [...prev, newCat]);
      toast.success('Category created');
    } catch {
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (isReadOnly) return;
    if (!confirm('Are you sure? Mappings will be removed.')) return;
    try {
      await clinicApi.deleteCategory(id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      toast.success('Category removed');
    } catch {
      toast.error('Failed to remove category');
    }
  };

  const handleUpdateCategoryWeight = async (cat: ClinicCategory, delta: number) => {
    if (isReadOnly) return;
    const nextWeight = Math.max(-10, Math.min(10, cat.weight + delta));
    try {
      const updated = await clinicApi.updateCategory({ ...cat, weight: nextWeight });
      setCategories((prev) => prev.map((c) => (c.id === cat.id ? updated : c)));
    } catch {
      toast.error('Failed to update category');
    }
  };

  // Suggests categories from the workspace's mail tag names now, rather than a
  // single arbitrary account's raw Gmail labels (settings-tab cutover) — tag
  // names are already account-agnostic and disambiguated.
  const mailTagsForSuggestQuery = trpc.mailTags.list.useQuery();

  const handleAISuggestCategories = async () => {
    if (isReadOnly) return;
    setIsSuggestingCats(true);
    try {
      const tagNames = (mailTagsForSuggestQuery.data?.items ?? []).map((t) => t.name);
      const suggestions = await clinicApi.suggestCategories(tagNames);
      for (const s of suggestions) {
        const newCat = await clinicApi.updateCategory(s);
        setCategories((prev) => [...prev, newCat]);
      }
      toast.success(`Created ${suggestions.length} categories`);
    } catch {
      toast.error('AI Suggestion failed');
    } finally {
      setIsSuggestingCats(false);
    }
  };

  const domainCategories = ['Revenue', 'Finance', 'Support', 'Internal', 'System', 'Noise'];

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/triage`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="display font-display text-3xl font-light tracking-tight">
              Clinic Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Categorize and prioritize your workspace inbox.
            </p>
          </div>
        </div>
      </div>

      {isReadOnly && (
        <div className="bg-muted-foreground/10 border border-border text-foreground p-4 rounded-xl flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">Read-only View</p>
            <p className="opacity-90">
              You have read-only access to this workspace. Modifying category mappings, label
              overrides, and account weights is disabled.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2 p-1 bg-muted/50 rounded-xl w-fit">
        <Button
          variant={activeTab === 'categories' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('categories')}
        >
          <FolderTree className="h-4 w-4 mr-2" /> Categories
        </Button>
        <Button
          variant={activeTab === 'mailTags' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('mailTags')}
        >
          <Tag className="h-4 w-4 mr-2" /> Mail Tags
        </Button>
        <Button
          variant={activeTab === 'domains' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('domains')}
        >
          <Globe className="h-4 w-4 mr-2" /> Domain Weights
        </Button>
        <Button
          variant={activeTab === 'accounts' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('accounts')}
        >
          <Mail className="h-4 w-4 mr-2" /> Account Access
        </Button>
        <Button
          variant={activeTab === 'analysis' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('analysis')}
        >
          <Sparkles className="h-4 w-4 mr-2" /> AI Analysis
        </Button>
      </div>

      {activeTab === 'mailTags' && <MailTagsSettings isReadOnly={isReadOnly} />}

      {activeTab === 'analysis' && <AnalysisSettings isReadOnly={isReadOnly} />}

      {activeTab === 'categories' && (
        <div className="max-w-xl">
          <Card className="rounded-panel border-border bg-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Custom Categories</CardTitle>
                <CardDescription>Define groups for summaries.</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleAddCategory}
                  disabled={isReadOnly}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="text-primary border-primary/30"
                  onClick={handleAISuggestCategories}
                  disabled={isSuggestingCats || isReadOnly}
                  title="AI Suggest Categories"
                >
                  {isSuggestingCats ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="p-3 rounded-xl border border-border/50 bg-background/50 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="font-semibold text-sm">{cat.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteCategory(cat.id)}
                      disabled={isReadOnly}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-border/10">
                    <span className="text-[10px] font-semibold text-muted-foreground">
                      Category Weight
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleUpdateCategoryWeight(cat, -1)}
                        disabled={isReadOnly}
                      >
                        <Minus className="h-2.5 w-2.5" />
                      </Button>
                      <span className="text-xs font-semibold w-4 text-center">{cat.weight}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleUpdateCategoryWeight(cat, 1)}
                        disabled={isReadOnly}
                      >
                        <Plus className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'domains' && (
        <Card className="rounded-panel border-border bg-card max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <CardTitle>Domain Category Weights</CardTitle>
            </div>
            <CardDescription>Weight domains identified by AI.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {domainCategories.map((cat) => (
              <WeightRow
                key={cat}
                label={cat}
                weight={domainWeights[cat] || 0}
                onUpdate={(delta) => updateDomainWeight(cat, delta)}
                disabled={isReadOnly}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {activeTab === 'accounts' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {accounts.map((acc) => (
            <AccountAccessCard
              key={acc.id}
              account={acc}
              members={members}
              isReadOnly={isReadOnly}
              isOwner={meQuery.data?.uid === acc.userId}
            />
          ))}
          {accounts.length === 0 && (
            <div className="p-12 border border-dashed rounded-xl text-center col-span-full">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <p className="text-muted-foreground">No accounts linked to this workspace.</p>
            </div>
          )}
        </div>
      )}

      <Card className="rounded-panel border-border bg-card">
        <CardContent className="p-6 flex gap-4 items-start">
          <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-1" />
          <div className="text-sm">
            <p className="font-semibold text-primary">Priority Calculation</p>
            <p className="text-muted-foreground mt-1">
              Score = (Highest Category Weight) + (Label Override Weight) + (Domain Weight).
              Categories help group your labels into logical buckets for summaries (e.g.
              &quot;Direct Client&quot; vs &quot;External Noise&quot;).
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AccountAccessCard({
  account,
  members,
  isReadOnly = false,
  isOwner,
}: {
  account: ClinicAccount;
  members: WorkspaceMember[];
  isReadOnly?: boolean;
  /** The roster is owner-only server-side; a shared account renders without it. */
  isOwner: boolean;
}) {
  const [perms, setPerms] = useState<AccountPermission[]>([]);
  const [isLoading, setIsLoading] = useState(isOwner);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userToRemove, setUserToRemove] = useState<AccountPermission | null>(null);

  const canManage = isOwner && !isReadOnly;

  const fetchPerms = useCallback(async () => {
    if (!isOwner) return;
    setIsLoading(true);
    try {
      const data = await clinicApi.getAccountPermissions(account.id);
      setPerms(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [account.id, isOwner]);

  useEffect(() => {
    fetchPerms();
  }, [fetchPerms]);

  const handleUpdate = async (userId: number, action: 'add' | 'remove') => {
    if (!canManage) return;
    try {
      await clinicApi.updateAccountPermissions(account.id, userId, action);
      toast.success(action === 'add' ? 'Access granted' : 'Access removed');
      fetchPerms();
    } catch {
      toast.error('Failed to update access');
    }
  };

  const availableMembers = members.filter(
    (m) => !perms.some((p) => p.userId === m.id) && m.id !== account.userId
  );

  return (
    <Card className="rounded-panel border-border bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{account.email}</CardTitle>
              <CardDescription>Manage user access.</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <h4 className="text-[10px] font-semibold text-muted-foreground">Authorized Users</h4>
          <div className="space-y-2">
            {!isOwner ? (
              <p className="text-xs text-muted-foreground italic">
                Shared with you — only the account owner can see who else has access.
              </p>
            ) : isLoading ? (
              <div className="flex justify-center py-4">
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : perms.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No additional users authorized.
              </p>
            ) : (
              perms.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-background/50 border border-border/30"
                >
                  <span className="text-xs font-medium">{p.email}</span>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setUserToRemove(p)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {canManage && (
          <div className="pt-4 border-t border-border/10">
            {isAdding ? (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <Select
                  value={selectedUserId || ''}
                  onValueChange={(val) => setSelectedUserId(val)}
                >
                  <SelectTrigger className="flex-1 h-9 bg-background/50 text-xs">
                    <SelectValue placeholder="Select member to authorize" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id.toString()}>
                        {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-9 px-3 text-xs"
                  disabled={!selectedUserId}
                  onClick={async () => {
                    if (!selectedUserId) return;
                    await handleUpdate(parseInt(selectedUserId, 10), 'add');
                    setIsAdding(false);
                    setSelectedUserId(null);
                  }}
                >
                  Add
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:bg-muted"
                  onClick={() => {
                    setIsAdding(false);
                    setSelectedUserId(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              availableMembers.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs gap-1.5 h-9 border-dashed border-primary/20 hover:border-primary/50 text-primary hover:bg-primary/5"
                  onClick={() => setIsAdding(true)}
                >
                  <Plus className="h-3.5 w-3.5" /> Add User
                </Button>
              )
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!userToRemove} onOpenChange={(open) => !open && setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke access for{' '}
              <span className="font-semibold text-foreground">{userToRemove?.email}</span>? They
              will no longer be able to view emails or threads from this account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!userToRemove) return;
                await handleUpdate(userToRemove.userId, 'remove');
                setUserToRemove(null);
              }}
            >
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function WeightRow({
  label,
  weight,
  onUpdate,
  disabled = false,
}: {
  label: string;
  weight: number;
  onUpdate: (delta: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-background/50">
      <span className="text-sm font-medium truncate mr-2" title={label}>
        {label === 'YELLOW_STAR' ? 'Starred' : label}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onUpdate(-1)}
          disabled={disabled}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <div
          className={cn(
            'w-7 text-center text-xs font-semibold',
            weight > 0 ? 'text-foreground' : weight < 0 ? 'text-foreground' : ''
          )}
        >
          {weight}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onUpdate(1)}
          disabled={disabled}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
